import { defineComponent, computed, PropType } from "vue";
import { ExternalLink } from "@lucide/vue";

import {
  CustomEmissionFactorSourceType,
  EmissionFactorSourceType,
  useEmissionFactorSources,
} from "@/dataProviders/EmissionFactorSources";

import { Emission_Factor_Type_Enum, useGetEmissionFactorSumQuery } from "@/graphql";

import { EmissionFactorType } from "@/features/BuildingProject";

import DataSourceCombobox from "@/components/Reusables/Inputs/DataSourceCombobox.vue";
import NumberInput from "@/components/Reusables/Inputs/NumberInput.vue";
import SelectInputWithKeyValue from "@/components/Reusables/Inputs/SelectInputWithKeyValue.vue";
import TextFieldInput from "@/components/Reusables/Inputs/TextFieldInput.vue";
import { useIndicators } from "@/dataProviders/Indicators";

import { selectBuiltInSource, selectExistingCustomSource, clearSource } from "./emissionFactorModel";

const EmissionConsumptionChoice = defineComponent({
  props: {
    modelValue: {
      type: Object as PropType<EmissionFactorType>,
      required: true,
    },
    usageLabel: {
      type: String as PropType<string>,
      default: "Consumption",
    },
    emissionFactorSources: {
      type: Object as PropType<EmissionFactorSourceType[]>,
      required: true,
    },
    customEmissionFactorSources: {
      type: Array as PropType<CustomEmissionFactorSourceType[]>,
      required: false,
      default: () => [],
    },
    emissionFactorTypes: {
      type: Object as PropType<Emission_Factor_Type_Enum[]>,
      required: true,
    },
    wasValidated: {
      type: Boolean as PropType<boolean>,
      required: false,
      default: false,
    },
    variant: {
      type: String as PropType<"installation" | "operational">,
      required: true,
    },
    yearOfCommissioning: {
      type: Number as PropType<number>,
      required: true,
    },
    yearOfConstruction: {
      type: Number as PropType<number>,
      required: true,
    },
    considerationPeriod: {
      type: Number as PropType<number>,
      required: true,
    },
    country: {
      type: String as PropType<string | undefined>,
      required: false,
      default: undefined,
    },
  },
  emits: {
    "update:modelValue": (_value: EmissionFactorType) => true,
  },
  setup(props, { emit }) {
    const emissionFactorSourcesProvider = useEmissionFactorSources();
    const { getIndicatorUnit } = useIndicators();

    const updateValue = (key: "type" | "value" | "description", value: unknown) => {
      emit("update:modelValue", { ...props.modelValue, [key]: value });
    };

    const selectedSource = computed(() => {
      return props.emissionFactorSources.find((source) => source.id === props.modelValue.sourceId);
    });

    const isCustomSourceSelected = computed(() => {
      return Boolean(props.modelValue.customSourceId);
    });

    const selectedCustomSource = computed<CustomEmissionFactorSourceType | undefined>(() => {
      if (!props.modelValue.customSourceId) return undefined;
      return props.customEmissionFactorSources.find((source) => source.id === props.modelValue.customSourceId);
    });

    const formatEmissionFactorType = (enumValue: string): string => {
      return enumValue
        .split("_")
        .map((word, index) => (index === 0 ? word.charAt(0) + word.slice(1).toLowerCase() : word.toLowerCase()))
        .join(" ");
    };

    const supplyTypes = computed(() => {
      const selectNone = { value: "", name: "Select supply type" };

      if (isCustomSourceSelected.value) {
        return [
          selectNone,
          ...props.emissionFactorTypes.map((enumValue) => ({
            value: enumValue,
            name: formatEmissionFactorType(enumValue),
          })),
        ];
      }

      if (!selectedSource.value) {
        return [selectNone];
      }

      return [
        selectNone,
        ...(selectedSource.value?.emissionFactors_aggregate.nodes
          .filter((node) => props.emissionFactorTypes.includes(node.emission_factor_type))
          .map((node) => ({
            value: node.emission_factor_type,
            name:
              selectedSource.value?.emissionFactorTypes.find(
                (x) => x.emissionFactorSourceId === selectedSource.value?.id && x.emissionFactorType === node.emission_factor_type
              )?.label || node.emission_factor_type,
          })) || []),
      ];
    });

    const consumptionLabel = computed(() => {
      const base = props.usageLabel;

      if (isCustomSourceSelected.value) {
        const unit = props.variant === "operational" ? "kWh/m²/year" : "kWh";
        return `${base} (${unit})`;
      }

      if (!selectedSource.value) {
        return base;
      }

      const sourceType = selectedSource.value.emissionFactorTypes.find((x) => x.emissionFactorType === props.modelValue.type);

      let unit;
      if (props.variant === "installation" && sourceType?.installationInputIndicatorType) {
        unit = getIndicatorUnit(
          sourceType.installationInputIndicatorType,
          sourceType.installationInputDenominatorUnit || undefined
        );
      } else if (props.variant === "operational" && sourceType?.operationalInputIndicatorType) {
        unit = getIndicatorUnit(
          sourceType.operationalInputIndicatorType,
          sourceType?.operationalInputDenominatorUnit || undefined
        );
      }

      return `${base} ${unit ? `(${unit})` : ""}`;
    });

    const emissionsLabel = computed(() => {
      if (isCustomSourceSelected.value) {
        return "Emissions (kgCO₂eq/kWh)";
      }

      if (!selectedSource.value || !props.modelValue.type) {
        return "Emissions";
      }

      const numerator = selectedSource.value.emissionFactors.find(
        (factor) => factor.emission_factor_type === props.modelValue.type
      )?.indicatorType?.unit;

      const sourceType = selectedSource.value.emissionFactorTypes.find((x) => x.emissionFactorType === props.modelValue.type);
      const denominator =
        props.variant === "operational"
          ? sourceType?.operationalInputIndicatorType?.unit
          : sourceType?.installationInputIndicatorType?.unit;

      if (!numerator && !denominator) return "Emissions";

      const formattedUnit = denominator ? [numerator, denominator].join("/") : numerator;

      return `Emissions (${formattedUnit})`;
    });

    const hasAnySourceSelected = computed(() => {
      return Boolean(props.modelValue.sourceId || props.modelValue.customSourceId);
    });

    const typeRequired = computed(() => hasAnySourceSelected.value);

    const typeErrorMsg = computed(() => {
      if (props.wasValidated && typeRequired.value && !props.modelValue.type) {
        return "Choose a supply type";
      }
      return undefined;
    });

    const valueRequired = computed(() => hasAnySourceSelected.value);

    const valueErrorMsg = computed(() => {
      return props.wasValidated && valueRequired.value && props.modelValue.value === null
        ? "Please enter a valid consumption"
        : undefined;
    });

    const onSourceModelUpdate = (sourceId: string) => {
      if (!sourceId) {
        emit("update:modelValue", clearSource());
        return;
      }

      const customRow = props.customEmissionFactorSources.find((s) => s.id === sourceId);
      if (customRow) {
        const next = selectExistingCustomSource(props.modelValue, sourceId, customRow.emissionFactorType);
        emit("update:modelValue", { ...next, description: customRow.description ?? undefined });
        return;
      }

      emit("update:modelValue", { ...selectBuiltInSource(props.modelValue, sourceId), description: undefined });
    };

    const comboboxModelValue = computed(() => props.modelValue.sourceId || props.modelValue.customSourceId || "");

    const isSupplyTypeLocked = computed(() => isCustomSourceSelected.value);

    const maxYear = computed(() => props.yearOfCommissioning + props.considerationPeriod);

    const isBuiltInSourceSelected = computed(() => Boolean(props.modelValue.sourceId) && !isCustomSourceSelected.value);

    const calculatedEmissionsEnabled = computed(() => {
      return (
        isBuiltInSourceSelected.value &&
        Boolean(props.modelValue.type) &&
        Number.isFinite(props.yearOfCommissioning) &&
        props.yearOfCommissioning > 0 &&
        Number.isFinite(props.considerationPeriod) &&
        props.considerationPeriod > 0
      );
    });

    // This is for countries where we don't country just one emission factor,
    // but need to sum and divide by consideration period
    const usesAnnualEmissionFactors = computed(() => props.country && ["Denmark", "Finland"].includes(props.country));

    const {
      result: emissionSumResult,
      loading: emissionSumLoading,
      error: emissionSumError,
    } = useGetEmissionFactorSumQuery(
      () => ({
        emissionFactorSourceId: props.modelValue.sourceId,
        emissionFactorType: props.modelValue.type as Emission_Factor_Type_Enum,
        minYear:
          props.variant === "operational"
            ? usesAnnualEmissionFactors.value
              ? props.yearOfCommissioning
              : 0
            : props.yearOfConstruction,
        maxYear:
          props.variant === "operational" ? (usesAnnualEmissionFactors.value ? maxYear.value : 0) : props.yearOfConstruction,
      }),
      { enabled: calculatedEmissionsEnabled }
    );

    const calculatedEmissions = computed<number | null>(() => {
      const sum = emissionSumResult.value?.emission_factor_aggregate.aggregate?.sum?.emission;

      if (props.variant === "operational") {
        if (!calculatedEmissionsEnabled.value) return null;
        if (props.considerationPeriod <= 0) return null;
        if (sum == null) return null;

        if (usesAnnualEmissionFactors.value) {
          return Number(sum) / props.considerationPeriod;
        }
      }

      return sum;
    });

    const isEmissionsLoading = computed(() => calculatedEmissionsEnabled.value && emissionSumLoading.value);

    const emissionsErrorTooltip = computed<string | undefined>(() => {
      if (!calculatedEmissionsEnabled.value || isEmissionsLoading.value) return undefined;
      if (!emissionSumError.value) return undefined;
      return emissionSumError.value.message || "Failed to calculate emissions";
    });

    const emissionsDisplayValue = computed<number | null>(() => {
      if (isCustomSourceSelected.value) {
        return selectedCustomSource.value ? Number(selectedCustomSource.value.emissionValue) : null;
      }
      if (isEmissionsLoading.value || emissionsErrorTooltip.value) return null;
      return calculatedEmissions.value;
    });

    return {
      props,
      emissionFactorSourcesProvider,
      updateValue,
      onSourceModelUpdate,
      supplyTypes,
      consumptionLabel,
      emissionsLabel,
      typeRequired,
      typeErrorMsg,
      valueRequired,
      valueErrorMsg,
      hasAnySourceSelected,
      isCustomSourceSelected,
      isSupplyTypeLocked,
      comboboxModelValue,
      selectedCustomSource,
      emissionsDisplayValue,
      isEmissionsLoading,
      emissionsErrorTooltip,
    };
  },
  render() {
    const {
      props,
      emissionFactorSourcesProvider,
      updateValue,
      onSourceModelUpdate,
      supplyTypes,
      consumptionLabel,
      emissionsLabel,
      typeRequired,
      typeErrorMsg,
      valueRequired,
      valueErrorMsg,
      hasAnySourceSelected,
      isCustomSourceSelected,
      isSupplyTypeLocked,
      comboboxModelValue,
      selectedCustomSource,
      emissionsDisplayValue,
      isEmissionsLoading,
      emissionsErrorTooltip,
    } = this;

    return (
      <>
        <div class="relative col-span-12 flex flex-col sm:col-span-6 lg:col-span-4">
          <DataSourceCombobox
            {...{
              modelValue: comboboxModelValue,
              "onUpdate:modelValue": onSourceModelUpdate,
              builtInSources: props.emissionFactorSources.map((source) => ({
                id: source.id,
                name: source.name,
              })),
              customSources: props.customEmissionFactorSources.map((source) => ({
                id: source.id,
                name: source.name,
                deleted: !!source.deletedAt,
              })),
              label: "Data source",
              placeholder: "Select data source",
            }}>
            {{
              labelSuffix: () =>
                props.modelValue.sourceId &&
                !isCustomSourceSelected &&
                emissionFactorSourcesProvider.getEmissionFactorDataSource(props.modelValue.sourceId).length > 2 ? (
                  <a
                    title="Data source"
                    aria-label="Open the data source reference"
                    class="focus-ring rounded-4 text-type-muted hover:text-app-blue-600 duration-fast ease-standard flex transition-colors"
                    href={emissionFactorSourcesProvider.getEmissionFactorDataSource(props.modelValue.sourceId)}
                    target="_blank"
                    rel="noopener noreferrer">
                    <ExternalLink
                      class="icon-sm"
                      stroke-width={1.5}
                    />
                  </a>
                ) : null,
            }}
          </DataSourceCombobox>
        </div>

        <SelectInputWithKeyValue
          class="col-span-12 sm:col-span-6 lg:col-span-4"
          {...{
            isDisabled: !hasAnySourceSelected || isSupplyTypeLocked,
            lockTooltip: isSupplyTypeLocked ? "Locked by custom datasource" : undefined,
            options: supplyTypes,
            value: props.modelValue.type,
            "onUpdate:value": (event: unknown) => updateValue("type", event),
            placeholder: "Select supply type",
            errorMsg: typeErrorMsg,
            label: "Supply type",
            required: typeRequired,
          }}
        />

        <div class="col-span-12 sm:col-span-6 lg:col-span-4">
          <NumberInput
            {...{
              value: emissionsDisplayValue,
              label: emissionsLabel,
              color: "primary",
              disabled: true,
              required: true,
              alignRight: true,
              isLoading: isEmissionsLoading,
              errorTooltip: emissionsErrorTooltip,
            }}
          />
        </div>

        <NumberInput
          class="col-span-12 sm:col-span-6 lg:col-span-4"
          {...{
            disabled: !hasAnySourceSelected || !props.modelValue.type,
            value: props.modelValue.value,
            label: consumptionLabel,
            color: "primary",
            "onUpdate:value": (event: unknown) => updateValue("value", event),
            errorMsg: valueErrorMsg,
            required: valueRequired,
            alignRight: true,
          }}
        />

        {/* A full-height side column at lg (RTLCA-5438): the four value fields form a 2×2 grid on
            the left and the optional note spans both of their rows on the right, so the card carries
            no dead space. Placed via explicit grid coordinates rather than DOM order so the tab
            order still ends on the optional field, after the numbers that drive the result. */}
        <div class="col-span-12 lg:col-span-4 lg:col-start-9 lg:row-span-2 lg:row-start-1">
          <TextFieldInput
            {...{
              value: props.modelValue.description ?? selectedCustomSource?.description ?? "",
              "onUpdate:value": (next: string) => updateValue("description", next),
              label: "Description",
              placeholder: "Optional",
              color: "primary",
              size: "min-h-9 w-full grow",
              class: "h-full",
            }}
          />
        </div>

        {/* Says WHY the other fields are locked. Before a source is picked, supply type and
            consumption sit disabled wearing required asterisks, which reads as a broken form rather
            than an order of operations — and a disabled control cannot carry a hover tooltip
            (Chrome fires no mouse events on one). Last in the DOM so the tab order note above
            still holds; a caption takes no focus. */}
        {!hasAnySourceSelected ? (
          <p class="t-caption text-type-muted col-span-12">Pick a data source first — it unlocks supply type and consumption.</p>
        ) : null}
      </>
    );
  },
});

export default EmissionConsumptionChoice;
